import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { auth, db } from "../firebase";

function formatTime(ts) {
  if (!ts) return "";
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  return isNaN(date)
    ? ""
    : date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
}

function formatDate(ts) {
  if (!ts) return "";
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  return isNaN(date) ? "" : date.toLocaleDateString();
}

function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentPrompt, setPaymentPrompt] = useState(null); // { notif, appointmentId, patientName }
  const [paymentSaving, setPaymentSaving] = useState(false);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setLoading(false);
          return;
        }

        // Fetch all notifications for the current user
        const notifSnap = await getDocs(
          query(
            collection(db, "notifications"),
            where("userId", "==", currentUser.uid)
          )
        );

        const allNotifications = notifSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // Deduplicate by appointmentId (keep latest by createdAt)
        const latestByAppt = new Map();
        for (const n of allNotifications) {
          const key = n.appointmentId || n.id;
          const time = n.createdAt?.toMillis ? n.createdAt.toMillis() : 0;
          const prev = latestByAppt.get(key);
          const prevTime = prev?.createdAt?.toMillis
            ? prev.createdAt.toMillis()
            : 0;
          if (!prev || time >= prevTime) {
            latestByAppt.set(key, n);
          }
        }
        const deduped = Array.from(latestByAppt.values());

        // Filter out notifications for completed or cancelled appointments
        const filteredNotifications = [];
        for (const notif of deduped) {
          if (notif.appointmentId) {
            try {
              const apptSnap = await getDoc(
                doc(db, "appointments", notif.appointmentId)
              );
              if (apptSnap.exists()) {
                const apptData = apptSnap.data();
                // Skip if appointment is completed or cancelled
                if (
                  apptData.status === "Completed" ||
                  apptData.status === "Cancelled"
                ) {
                  // Delete the notification from database
                  await deleteDoc(doc(db, "notifications", notif.id));
                  continue;
                }
              }
            } catch (error) {
              console.error("Error checking appointment status:", error);
            }
          }
          filteredNotifications.push(notif);
        }

        // Sort by createdAt descending
        filteredNotifications.sort((a, b) => {
          const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return bTime - aTime;
        });

        setNotifications(filteredNotifications);

        // Mark all notifications as read (not only deduped)
        const markAsReadPromises = allNotifications
          .filter((n) => n.read === false)
          .map((n) =>
            updateDoc(doc(db, "notifications", n.id), { read: true })
          );

        if (markAsReadPromises.length > 0) {
          await Promise.all(markAsReadPromises);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
    // Refresh notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleMarkComplete = async (notif) => {
    try {
      if (notif.appointmentId) {
        const apptRef = doc(db, "appointments", notif.appointmentId);
        const apptSnap = await getDoc(apptRef);

        if (apptSnap.exists()) {
          const apptData = apptSnap.data();
          const patientId = apptData.patientId;

          if (patientId) {
            const patientSnap = await getDoc(doc(db, "patients", patientId));
            const paymentType = patientSnap.exists()
              ? patientSnap.data().paymentType
              : null;

            // For postpaid patients, ask for payment received before completing
            if (paymentType === "postpaid") {
              setPaymentPrompt({
                notif,
                appointmentId: notif.appointmentId,
                patientName:
                  apptData.patientName || notif.patientName || "Patient",
              });
              return;
            }
          }

          await updateDoc(apptRef, {
            status: "Completed",
          });
        }

        await deleteDoc(doc(db, "notifications", notif.id));
        setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      }
    } catch (error) {
      console.error("Error marking complete:", error);
    }
  };

  const handlePaymentResponse = async (paymentReceived) => {
    if (!paymentPrompt?.appointmentId) return;

    setPaymentSaving(true);
    try {
      await updateDoc(doc(db, "appointments", paymentPrompt.appointmentId), {
        status: "Completed",
        paymentStatus: paymentReceived ? "paid" : "unpaid",
      });

      await deleteDoc(doc(db, "notifications", paymentPrompt.notif.id));
      setNotifications((prev) =>
        prev.filter((n) => n.id !== paymentPrompt.notif.id)
      );
      setPaymentPrompt(null);
    } catch (error) {
      console.error("Error updating payment:", error);
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleClosePaymentPrompt = () => {
    if (paymentSaving) return;
    setPaymentPrompt(null);
  };

  const handleMarkNotComplete = async (notif) => {
    try {
      if (notif.appointmentId) {
        await updateDoc(doc(db, "appointments", notif.appointmentId), {
          status: "Not Completed",
        });
        await deleteDoc(doc(db, "notifications", notif.id));
        setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      }
    } catch (error) {
      console.error("Error marking not complete:", error);
    }
  };

  const handleMarkCancelled = async (notif) => {
    try {
      if (notif.appointmentId) {
        await updateDoc(doc(db, "appointments", notif.appointmentId), {
          status: "Cancelled",
        });
        await deleteDoc(doc(db, "notifications", notif.id));
        setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      }
    } catch (error) {
      console.error("Error marking cancelled:", error);
    }
  };

  const handleBackClick = async () => {
    try {
      // Delete all "starts in X minutes" notifications
      const startsInNotifications = notifications.filter(
        (notif) =>
          notif.message &&
          notif.message.includes("starts in") &&
          notif.message.includes("minutes")
      );

      if (startsInNotifications.length > 0) {
        const deletePromises = startsInNotifications.map((notif) =>
          deleteDoc(doc(db, "notifications", notif.id))
        );
        await Promise.all(deletePromises);

        // Mark related appointments to suppress recreated "started" notifications
        const apptIds = Array.from(
          new Set(
            startsInNotifications.map((n) => n.appointmentId).filter(Boolean)
          )
        );

        const updatePromises = apptIds.map((apptId) =>
          updateDoc(doc(db, "appointments", apptId), {
            suppressStartedNotification: true,
          }).catch((e) => console.warn("Failed to set suppression", e))
        );
        await Promise.all(updatePromises);
      }
    } catch (error) {
      console.error("Error deleting notifications:", error);
    } finally {
      navigate(-1);
    }
  };

  return (
    <div className="appts-layout">
      <header className="appts-header">
        <div className="appts-header-left">
          <button
            type="button"
            className="appts-icon-btn"
            onClick={handleBackClick}
            aria-label="Back"
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <div>
            <h1>Notifications</h1>
            <p>
              {notifications.length} notification
              {notifications.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
      </header>

      <main className="appts-main">
        {loading && <p className="appts-muted">Loading notifications...</p>}

        {!loading && notifications.length === 0 && (
          <div className="appts-empty">
            <span className="material-symbols-outlined">notifications_off</span>
            <p>No notifications</p>
          </div>
        )}

        {!loading &&
          notifications.map((notif) => (
            <div key={notif.id} className="appts-card notif-card">
              <div className="notif-left">
                <div className="notif-avatar">
                  <span className="material-symbols-outlined">
                    {notif.type === "started"
                      ? "play_circle"
                      : notif.type === "appointment_ended"
                      ? "schedule"
                      : "notifications"}
                  </span>
                </div>
              </div>
              <div className="notif-main">
                <div className="notif-header">
                  <h3 className="notif-title">{notif.patientName}</h3>
                  <span className="notif-time-badge">
                    {formatTime(notif.startTime)}
                  </span>
                </div>
                <p className="notif-message">{notif.message}</p>
                <p className="notif-date">{formatDate(notif.startTime)}</p>

                <div className="notif-actions">
                  {notif.type === "appointment_ended" ? (
                    <>
                      <button
                        className="notif-btn notif-btn-complete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkComplete(notif);
                        }}
                      >
                        <span className="material-symbols-outlined">
                          check_circle
                        </span>
                        Complete
                      </button>
                      <button
                        className="notif-btn notif-btn-cancel"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkNotComplete(notif);
                        }}
                        style={{ background: "var(--warning)", color: "white" }}
                      >
                        <span className="material-symbols-outlined">close</span>
                        Not Complete
                      </button>
                    </>
                  ) : notif.type === "started" ? (
                    (() => {
                      const start = notif.startTime?.toDate
                        ? notif.startTime.toDate()
                        : new Date(notif.startTime);
                      const showActions =
                        start && !isNaN(start) && Date.now() >= start.getTime();
                      if (!showActions) return null; // Hide actions until appointment start time
                      return (
                        <>
                          <button
                            className="notif-btn notif-btn-complete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkComplete(notif);
                            }}
                          >
                            <span className="material-symbols-outlined">
                              check_circle
                            </span>
                            Complete
                          </button>
                          <button
                            className="notif-btn notif-btn-cancel"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkCancelled(notif);
                            }}
                          >
                            <span className="material-symbols-outlined">
                              cancel
                            </span>
                            Cancel
                          </button>
                        </>
                      );
                    })()
                  ) : (
                    <>
                      <button
                        className="notif-btn notif-btn-complete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkComplete(notif);
                        }}
                      >
                        <span className="material-symbols-outlined">
                          check_circle
                        </span>
                        Complete
                      </button>
                      <button
                        className="notif-btn notif-btn-cancel"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkCancelled(notif);
                        }}
                      >
                        <span className="material-symbols-outlined">
                          cancel
                        </span>
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
      </main>

      {paymentPrompt && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "16px",
          }}
          onClick={handleClosePaymentPrompt}
        >
          <div
            style={{
              background: "var(--bg-primary, #fff)",
              borderRadius: "12px",
              padding: "24px",
              width: "100%",
              maxWidth: "400px",
              boxShadow: "0 12px 36px rgba(0,0,0,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 8px", fontSize: "20px" }}>
              Payment Received?
            </h3>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={() => handlePaymentResponse(false)}
                disabled={paymentSaving}
                style={{
                  padding: "12px 20px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color, #e5e7eb)",
                  background: "transparent",
                  color: "var(--text-primary)",
                  fontWeight: "600",
                  cursor: paymentSaving ? "not-allowed" : "pointer",
                  opacity: paymentSaving ? 0.6 : 1,
                }}
              >
                No
              </button>
              <button
                type="button"
                onClick={() => handlePaymentResponse(true)}
                disabled={paymentSaving}
                style={{
                  padding: "12px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background: "var(--success, #10b981)",
                  color: "white",
                  fontWeight: "600",
                  cursor: paymentSaving ? "not-allowed" : "pointer",
                  opacity: paymentSaving ? 0.7 : 1,
                }}
              >
                {paymentSaving ? "Saving..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}

      <nav className="dash-bottom-nav">
        <button className="dash-nav-item" onClick={() => navigate("/doctor")}>
          <span className="material-symbols-outlined">home</span>
          <span>Home</span>
        </button>
        <button
          className="dash-nav-item"
          onClick={() => navigate("/appointments")}
        >
          <span className="material-symbols-outlined">calendar_month</span>
          <span>Appointments</span>
        </button>
        <button className="dash-nav-item" onClick={() => navigate("/patients")}>
          <span className="material-symbols-outlined">groups</span>
          <span>Patients</span>
        </button>
        <button className="dash-nav-item" onClick={() => navigate("/settings")}>
          <span className="material-symbols-outlined fill-current">person</span>
          <span>Profile</span>
        </button>
      </nav>
    </div>
  );
}

export default NotificationsPage;
